import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { PageLoader } from "@/components/ui/PageLoader";
import { useAccount } from "@/features/account/hooks/useAccount";
import { useStaffRole } from "../hooks/useStaff";

/**
 * Hiding the nav item is not access control: the URL is typeable. This refuses to render
 * the support views for a session with no staff role, and the server refuses the data
 * regardless, so the two disagree only in what the reader sees, never in what they get.
 */
export function StaffOnly({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const { role, isLoading } = useStaffRole();
  const { error } = useAccount();

  if (isLoading) return <PageLoader />;

  // A lookup that failed says nothing about whether this account is staff, so it must
  // not be reported as a refusal.
  if (error) {
    return (
      <div className="mx-auto max-w-xl rounded-lg border border-border bg-surface px-6 py-8 text-center">
        <h1 className="text-lg font-semibold text-heading">{t("staff.error.title")}</h1>
        <p className="mt-2 text-sm text-muted">{t("staff.error.body")}</p>
      </div>
    );
  }

  if (!role) {
    return (
      <div className="mx-auto max-w-xl rounded-lg border border-border bg-surface px-6 py-8 text-center">
        <h1 className="text-lg font-semibold text-heading">{t("staff.denied.title")}</h1>
        <p className="mt-2 text-sm text-muted">{t("staff.denied.body")}</p>
      </div>
    );
  }

  return <>{children}</>;
}
